import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Circle, Lock } from 'lucide-react';
import { callingsApi } from '../../services/api';
import Modal from '../shared/Modal';
import Badge from '../shared/Badge';
import { formatDateShort } from '../../utils/formatters';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const WARDS_LIST = [
  { key: 'step_sustain_arvore_grande', label: 'Ala Árvore Grande' },
  { key: 'step_sustain_barcelona', label: 'Ala Barcelona' },
  { key: 'step_sustain_votorantim', label: 'Ala Votorantim' },
  { key: 'step_sustain_rio_acima', label: 'Ala Rio Acima' },
  { key: 'step_sustain_sao_roque', label: 'Ala São Roque' },
  { key: 'step_sustain_mairinque', label: 'Ramo Mairinque' },
  { key: 'step_sustain_piedade', label: 'Ramo Piedade' },
];

const WARD_STEP_MAP: Record<string, string> = {
  'Ala Árvore Grande': 'step_sustain_arvore_grande',
  'Ala Barcelona': 'step_sustain_barcelona',
  'Ala Votorantim': 'step_sustain_votorantim',
  'Ala Rio Acima': 'step_sustain_rio_acima',
  'Ala São Roque': 'step_sustain_sao_roque',
  'Ramo Mairinque': 'step_sustain_mairinque',
  'Ramo Piedade': 'step_sustain_piedade',
};

interface Props {
  calling: any;
  onClose: () => void;
}

export default function CallingDetail({ calling: initialCalling, onClose }: Props) {
  const [calling, setCalling] = useState<any>(initialCalling);
  const [interviewBy, setInterviewBy] = useState(calling.step_interview_by || '');
  const [interviewDate, setInterviewDate] = useState(calling.step_interview_date || '');
  const [setApartBy, setSetApartBy] = useState(calling.step_set_apart_by || '');
  const [setApartDate, setSetApartDate] = useState(calling.step_set_apart_date || '');
  const [notes, setNotes] = useState(calling.notes || '');
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const queryClient = useQueryClient();

  const stepMutation = useMutation({
    mutationFn: (params: { step: string; value: boolean; options?: Record<string, string> }) =>
      callingsApi.updateStep(calling.id, params.step, params.value, params.options),
    onSuccess: (res) => {
      setCalling(res.data);
      queryClient.invalidateQueries({ queryKey: ['callings'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Etapa atualizada!');
    },
    onError: (err: any) => toast.error(err.message || 'Erro ao atualizar'),
  });

  const archiveMutation = useMutation({
    mutationFn: () => callingsApi.archive(calling.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callings'] });
      toast.success('Chamado arquivado');
      onClose();
    },
    onError: (err: any) => toast.error(err.message),
  });

  const notesMutation = useMutation({
    mutationFn: () => callingsApi.update(calling.id, { notes }),
    onSuccess: () => toast.success('Observações salvas'),
    onError: (err: any) => toast.error(err.message),
  });

  const updateStep = (step: string, value: boolean, options?: Record<string, string>) => {
    stepMutation.mutate({ step, value, options });
  };

  const interviewDone = !!(calling.step_interview_by && calling.step_interview_date);

  // Determine which sustain steps to show
  const sustainStepsToShow = calling.sustain_scope === 'ala'
    ? WARDS_LIST.filter(w => w.key === WARD_STEP_MAP[calling.ward])
    : WARDS_LIST;

  const allSustainsDone = sustainStepsToShow.every(w => calling[w.key]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Detalhe do Chamado"
      size="lg"
      footer={
        <div className="space-y-2">
          {calling.status === 'in_progress' && (
            showArchiveConfirm ? (
              <div className="flex gap-3">
                <button onClick={() => setShowArchiveConfirm(false)} className="btn-ghost flex-1">Cancelar</button>
                <button
                  onClick={() => archiveMutation.mutate()}
                  disabled={archiveMutation.isPending}
                  className="flex-1 py-2 px-4 rounded-xl text-sm font-medium bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors"
                >
                  {archiveMutation.isPending ? 'Arquivando...' : 'Confirmar arquivo'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowArchiveConfirm(true)}
                className="w-full py-2 px-4 rounded-xl text-sm font-medium text-gray-500 border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Arquivar chamado
              </button>
            )
          )}
          <button onClick={onClose} className="btn-primary w-full">Fechar</button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Header info */}
        <div className="p-3 bg-gray-50 rounded-xl">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-semibold text-gray-800">{calling.person_name}</p>
              <p className="text-sm text-gray-600">{calling.calling_name}</p>
              <p className="text-xs text-gray-400">{calling.ward} · Rec. por {calling.recommended_by}</p>
            </div>
            <Badge
              label={calling.type === 'chamado' ? 'Chamado' : 'Desobrigação'}
              variant={calling.type === 'chamado' ? 'blue' : 'orange'}
            />
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-3">
          {/* Step 1: Presidency approval */}
          <StepRow
            label="Aprovação em Presidência"
            done={!!calling.step_presidency_approval}
            date={calling.step_presidency_approval_date}
            locked={false}
            onToggle={(v) => updateStep('step_presidency_approval', v, { date: new Date().toISOString().split('T')[0] })}
            loading={stepMutation.isPending}
          />

          {/* Step 2: Bishop approval */}
          <StepRow
            label="Aprovação do Bispo"
            done={!!calling.step_bishop_approval}
            date={calling.step_bishop_approval_date}
            locked={!calling.step_presidency_approval}
            onToggle={(v) => updateStep('step_bishop_approval', v, { date: new Date().toISOString().split('T')[0] })}
            loading={stepMutation.isPending}
          />

          {/* Step 3: High council */}
          <StepRow
            label="Apoio no Sumo Conselho"
            done={!!calling.step_high_council_sustain}
            date={calling.step_high_council_sustain_date}
            locked={!calling.step_bishop_approval}
            onToggle={(v) => updateStep('step_high_council_sustain', v, { date: new Date().toISOString().split('T')[0] })}
            loading={stepMutation.isPending}
          />

          {/* Step 4: Interview */}
          <div className={clsx(
            'flex items-start gap-3 p-3 rounded-xl',
            !calling.step_high_council_sustain ? 'opacity-40' : 'border border-gray-100'
          )}>
            <div className="flex-shrink-0 mt-0.5">
              {interviewDone ? (
                <CheckCircle size={20} className="text-green-500" />
              ) : calling.step_high_council_sustain ? (
                <Circle size={20} className="text-gray-300" />
              ) : (
                <Lock size={20} className="text-gray-200" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700">Entrevista</p>
              {calling.step_high_council_sustain && (
                <div className="mt-2 space-y-2">
                  <input
                    type="text"
                    value={interviewBy}
                    onChange={e => setInterviewBy(e.target.value)}
                    placeholder="Quem entrevistou"
                    className="input text-sm"
                  />
                  <input
                    type="date"
                    value={interviewDate}
                    onChange={e => setInterviewDate(e.target.value)}
                    className="input text-sm"
                  />
                  {(interviewBy !== calling.step_interview_by || interviewDate !== calling.step_interview_date) && interviewBy && interviewDate && (
                    <button
                      onClick={() => updateStep('step_interview', true, { by: interviewBy, date: interviewDate })}
                      disabled={stepMutation.isPending}
                      className="btn-primary text-sm py-1.5"
                    >
                      Salvar entrevista
                    </button>
                  )}
                  {interviewDone && (
                    <p className="text-xs text-green-600">
                      ✓ {calling.step_interview_by} — {formatDateShort(calling.step_interview_date)}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Step 5: Sustain in units */}
          <div className={clsx('p-3 rounded-xl', !interviewDone ? 'opacity-40' : 'border border-gray-100')}>
            <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              {!interviewDone && <Lock size={16} className="text-gray-300" />}
              Apoio nas Unidades
              {calling.sustain_scope === 'estaca' && (
                <span className="text-xs text-gray-400">(estaca)</span>
              )}
            </p>
            {interviewDone && (
              <div className="space-y-2">
                {sustainStepsToShow.map(ward => (
                  <label key={ward.key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!calling[ward.key]}
                      onChange={(e) => updateStep(ward.key, e.target.checked)}
                      disabled={stepMutation.isPending}
                      className="w-4 h-4 rounded accent-brand-500"
                    />
                    <span className="text-sm text-gray-700">{ward.label}</span>
                    {!!calling[ward.key] && <CheckCircle size={14} className="text-green-500" />}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Step 6: LCR */}
          <StepRow
            label="Registro no LCR"
            done={!!calling.step_lcr_recorded}
            date={calling.step_lcr_recorded_date}
            locked={!interviewDone}
            onToggle={(v) => updateStep('step_lcr_recorded', v, { date: new Date().toISOString().split('T')[0] })}
            loading={stepMutation.isPending}
          />

          {/* Step 7: Set apart (chamado only) */}
          {calling.type === 'chamado' && (
            <div className={clsx(
              'flex items-start gap-3 p-3 rounded-xl',
              !interviewDone ? 'opacity-40' : 'border border-gray-100'
            )}>
              <div className="flex-shrink-0 mt-0.5">
                {calling.step_set_apart_by ? (
                  <CheckCircle size={20} className="text-green-500" />
                ) : interviewDone ? (
                  <Circle size={20} className="text-gray-300" />
                ) : (
                  <Lock size={20} className="text-gray-200" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-700">Designação</p>
                {interviewDone && (
                  <div className="mt-2 space-y-2">
                    <input
                      type="text"
                      value={setApartBy}
                      onChange={e => setSetApartBy(e.target.value)}
                      placeholder="Quem designou"
                      className="input text-sm"
                    />
                    <input
                      type="date"
                      value={setApartDate}
                      onChange={e => setSetApartDate(e.target.value)}
                      className="input text-sm"
                    />
                    {setApartBy && setApartDate && (
                      setApartBy !== calling.step_set_apart_by || setApartDate !== calling.step_set_apart_date
                    ) && (
                      <button
                        onClick={() => updateStep('step_set_apart', true, { by: setApartBy, date: setApartDate })}
                        disabled={stepMutation.isPending}
                        className="btn-primary text-sm py-1.5"
                      >
                        Salvar designação
                      </button>
                    )}
                    {calling.step_set_apart_by && (
                      <p className="text-xs text-green-600">
                        ✓ {calling.step_set_apart_by} — {formatDateShort(calling.step_set_apart_date)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="p-3 border border-gray-100 rounded-xl">
            <p className="text-sm font-medium text-gray-700 mb-2">Observações</p>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="input resize-none text-sm"
              rows={3}
              placeholder="Observações sobre este chamado..."
            />
            {notes !== calling.notes && (
              <button
                onClick={() => notesMutation.mutate()}
                disabled={notesMutation.isPending}
                className="btn-primary text-sm py-1.5 mt-2"
              >
                Salvar
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

interface StepRowProps {
  label: string;
  done: boolean;
  date?: string | null;
  locked: boolean;
  onToggle: (v: boolean) => void;
  loading: boolean;
}

function StepRow({ label, done, date, locked, onToggle, loading }: StepRowProps) {
  return (
    <div className={clsx(
      'flex items-center gap-3 p-3 rounded-xl',
      locked ? 'opacity-40' : 'border border-gray-100'
    )}>
      <button
        onClick={() => !locked && onToggle(!done)}
        disabled={locked || loading}
        className={clsx('flex-shrink-0 transition-colors', locked ? 'cursor-not-allowed' : 'cursor-pointer')}
      >
        {done ? (
          <CheckCircle size={20} className="text-green-500" />
        ) : locked ? (
          <Lock size={20} className="text-gray-200" />
        ) : (
          <Circle size={20} className="text-gray-300 hover:text-brand-400" />
        )}
      </button>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {done && date && (
          <p className="text-xs text-green-600 mt-0.5">✓ {formatDateShort(date)}</p>
        )}
      </div>
    </div>
  );
}
